import {
  JobStatus,
  LeadStage,
  ProInvoice,
  ProInvoiceStatus,
  Quote,
  QuoteLine,
  QuoteStatus,
  TimeEntry,
} from '../types/pro'

/** Danish kroner, no decimals — amounts in this trade are always round enough. */
export function kr(value: number): string {
  return `${Math.round(value).toLocaleString('da-DK')} kr`
}

export function krPrecise(value: number): string {
  return `${value.toLocaleString('da-DK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} kr`
}

export function formatDate(iso: string): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('da-DK', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export function formatShortDate(iso: string): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('da-DK', {
    day: '2-digit',
    month: '2-digit',
  })
}

export function formatTime(iso: string): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleTimeString('da-DK', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function daysSince(iso: string): number {
  if (!iso) return 0
  const diff = Date.now() - new Date(iso).getTime()
  return Math.max(0, Math.floor(diff / 86400000))
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

/** Monday of the week containing `date`. */
export function startOfWeek(date: Date): Date {
  const result = new Date(date)
  const day = result.getDay()
  result.setDate(result.getDate() + (day === 0 ? -6 : 1 - day))
  result.setHours(0, 0, 0, 0)
  return result
}

export function addDays(date: Date, days: number): Date {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

export function toISODate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0',
  )}-${String(date.getDate()).padStart(2, '0')}`
}

// ---------------------------------------------------------------------------
// Labels + status colours. One source of truth so a colour always means the
// same thing no matter which screen you are looking at.
// ---------------------------------------------------------------------------

export interface StatusMeta {
  label: string
  /** Tailwind classes for a chip. */
  chip: string
  /** Tailwind background for a dot or bar. */
  dot: string
}

export const LEAD_STAGES: { id: LeadStage; label: string; dot: string }[] = [
  { id: 'nyt', label: 'Nyt lead', dot: 'bg-sky-500' },
  { id: 'kontaktet', label: 'Kontaktet', dot: 'bg-blue-500' },
  { id: 'besigtigelse', label: 'Besigtigelse', dot: 'bg-indigo-500' },
  { id: 'tilbud-skal-laves', label: 'Tilbud skal laves', dot: 'bg-amber-500' },
  { id: 'tilbud-sendt', label: 'Tilbud sendt', dot: 'bg-brand-500' },
  { id: 'vundet', label: 'Vundet', dot: 'bg-emerald-500' },
  { id: 'tabt', label: 'Tabt', dot: 'bg-stone-400' },
]

export const JOB_STATUS: Record<JobStatus, StatusMeta> = {
  'ikke-planlagt': {
    label: 'Ikke planlagt',
    chip: 'bg-stone-100 text-stone-700',
    dot: 'bg-stone-400',
  },
  planlagt: {
    label: 'Planlagt',
    chip: 'bg-blue-50 text-blue-700',
    dot: 'bg-blue-500',
  },
  'i-gang': {
    label: 'I gang',
    chip: 'bg-amber-50 text-amber-700',
    dot: 'bg-amber-500',
  },
  afventer: {
    label: 'Afventer',
    chip: 'bg-purple-50 text-purple-700',
    dot: 'bg-purple-500',
  },
  faerdig: {
    label: 'Færdig',
    chip: 'bg-emerald-50 text-emerald-700',
    dot: 'bg-emerald-500',
  },
  faktureret: {
    label: 'Faktureret',
    chip: 'bg-stone-800 text-white',
    dot: 'bg-stone-800',
  },
}

export const QUOTE_STATUS: Record<QuoteStatus, StatusMeta> = {
  kladde: {
    label: 'Kladde',
    chip: 'bg-stone-100 text-stone-700',
    dot: 'bg-stone-400',
  },
  sendt: {
    label: 'Sendt',
    chip: 'bg-blue-50 text-blue-700',
    dot: 'bg-blue-500',
  },
  set: {
    label: 'Set',
    chip: 'bg-indigo-50 text-indigo-700',
    dot: 'bg-indigo-500',
  },
  accepteret: {
    label: 'Accepteret',
    chip: 'bg-emerald-50 text-emerald-700',
    dot: 'bg-emerald-500',
  },
  afvist: {
    label: 'Afvist',
    chip: 'bg-red-50 text-red-700',
    dot: 'bg-red-500',
  },
  udloebet: {
    label: 'Udløbet',
    chip: 'bg-amber-50 text-amber-700',
    dot: 'bg-amber-500',
  },
}

export const INVOICE_STATUS: Record<ProInvoiceStatus, StatusMeta> = {
  kladde: {
    label: 'Kladde',
    chip: 'bg-stone-100 text-stone-700',
    dot: 'bg-stone-400',
  },
  sendt: {
    label: 'Sendt',
    chip: 'bg-blue-50 text-blue-700',
    dot: 'bg-blue-500',
  },
  betalt: {
    label: 'Betalt',
    chip: 'bg-emerald-50 text-emerald-700',
    dot: 'bg-emerald-500',
  },
  forfalden: {
    label: 'Forfalden',
    chip: 'bg-red-50 text-red-700',
    dot: 'bg-red-500',
  },
}

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

export function lineTotal(line: {
  quantity: number
  unitPrice: number
  discount?: number
}): number {
  const gross = line.quantity * line.unitPrice
  return gross - gross * ((line.discount ?? 0) / 100)
}

export interface Totals {
  subtotal: number
  vat: number
  total: number
}

export function totals(
  lines: { quantity: number; unitPrice: number; discount?: number }[],
  vatRate = 0.25,
): Totals {
  const subtotal = lines.reduce((sum, line) => sum + lineTotal(line), 0)
  const vat = subtotal * vatRate
  return { subtotal, vat, total: subtotal + vat }
}

export function quoteTotal(quote: Quote, vatRate = 0.25): number {
  return totals(quote.lines, vatRate).total
}

export function invoiceTotal(invoice: ProInvoice, vatRate = 0.25): number {
  return totals(invoice.lines, vatRate).total
}

export function hoursOnJob(entries: TimeEntry[], jobId: string): number {
  return entries
    .filter((entry) => entry.jobId === jobId)
    .reduce((sum, entry) => sum + entry.hours, 0)
}

export function newLineId(): string {
  return `l-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

/** Turns 9045 seconds into "02:30:45". */
export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':')
}

export function nextNumber(existing: string[], prefix: string): string {
  const year = new Date().getFullYear()
  const numbers = existing
    .map((value) => parseInt(value.split('-').pop() || '0', 10))
    .filter((value) => !Number.isNaN(value))
  const next = numbers.length === 0 ? 1 : Math.max(...numbers) + 1
  return `${prefix}-${year}-${String(next).padStart(4, '0')}`
}

/**
 * Job numbers stay in the short form the company already uses — O001, O002 —
 * rather than the year-based form used for quotes and invoices.
 */
export const JOB_NO_PATTERN = /^O(\d{3,})$/

export function nextJobNo(existing: string[]): string {
  const numbers = existing
    .map((value) => value?.match(JOB_NO_PATTERN)?.[1])
    .filter(Boolean)
    .map((value) => parseInt(value as string, 10))
  const next = numbers.length === 0 ? 1 : Math.max(...numbers) + 1
  return `O${String(next).padStart(3, '0')}`
}

export function emptyQuoteLine(): QuoteLine {
  return {
    id: newLineId(),
    description: '',
    quantity: 1,
    unit: 'stk',
    unitPrice: 0,
    discount: 0,
  }
}
